import {useSelector} from "react-redux";
import {RootState} from "../../slices/RootReducer";
import {useEffect, useState} from "react";
import {AddonBillingType, AddonDetails} from "../../api/entities/Addon";
import {BusinessBuilder} from "../../slices/NewOrderCreateSlice";
import {ProductDetails} from "../../api/entities/Product";
import {GetTotalOneTimePrice} from "../../utils/PlanUtils";
import {GetAddonPrice, GetDiscountPrice} from "../../utils/ProductUtils";
import {ProductGroup} from "../../api/entities/ProductGroup";

export interface AddonLedgerItem {
    name: string;
    quantity: number;
    pricePerUnit: number;
    oneTimePrice: number;
    recurringPrice: number;
    startDate?: string;
    contractLength?: number;
    addonBillingType: AddonBillingType;
}

export interface INewOrderDetails {
    totalOneTimePrice: number;
    productId: string;
    addons: AddonDetails[];
    totalMonthlyPrice?: number;
    discount?: number;
    isOverrideSetupFee: boolean;
    overrideSetupFee: number;
    addonLedger: AddonLedgerItem[];
    productDetails?: ProductDetails;
    productGroup?: ProductGroup;
}

export const CreateAddonLedger = (
    addons: AddonDetails[],
    productGroup: ProductGroup,
    product: ProductDetails,
): AddonLedgerItem[] => {
    const ledger: AddonLedgerItem[] = [];
    addons.forEach((addon) => {
        const oneTimePrice =
            addon.addonBillingType === AddonBillingType.OneTime
                ? GetAddonPrice(addon, productGroup.id)
                : 0;
        const recurringPrice =
            addon.addonBillingType === AddonBillingType.ParentRecurring ||
            addon.addonBillingType === AddonBillingType.IndividualRecurring
                ? GetAddonPrice(addon, productGroup.id)
                : 0;

        let contractLength = addon.contractLength;
        if (!contractLength && addon.contractLengthDictionary) {
            contractLength =
                addon.contractLengthDictionary[product.contractLengthMonths];
        }

        ledger.push({
            name: addon.name,
            quantity: 1,
            pricePerUnit: oneTimePrice,
            oneTimePrice: oneTimePrice,
            recurringPrice: recurringPrice,
            startDate: addon.startDate,
            contractLength: contractLength,
            addonBillingType: addon.addonBillingType,
        });
    });
    return ledger;
};

export const CreateAddonLedgerFromAddonDetails = (
    addons: AddonDetails[],
    product: ProductDetails,
): AddonLedgerItem[] => {
    const ledger: AddonLedgerItem[] = [];
    addons.forEach((addon) => {
        const oneTimePrice =
            addon.addonBillingType === AddonBillingType.OneTime ? addon.price : 0;
        const recurringPrice =
            addon.addonBillingType === AddonBillingType.ParentRecurring ||
            addon.addonBillingType === AddonBillingType.IndividualRecurring
                ? addon.price
                : 0;

        let contractLength = addon.contractLength;
        if (!contractLength && addon.contractLengthDictionary) {
            contractLength =
                addon.contractLengthDictionary[product.contractLengthMonths];
        }

        ledger.push({
            name: addon.name,
            quantity: 1,
            pricePerUnit: oneTimePrice,
            oneTimePrice: oneTimePrice,
            recurringPrice: recurringPrice,
            startDate: addon.startDate,
            contractLength: contractLength,
            addonBillingType: addon.addonBillingType,
        });
    });
    return ledger;
};

export const useNewOrderDetails = (): INewOrderDetails => {
    const {businessBuilder} = useSelector((state: RootState) => state.newOrder);
    const [totalOneTimePrice, setTotalOneTimePrice] = useState(0);
    const [totalMonthlyPrice, setTotalMonthlyPrice] = useState(0);
    const [addonLedger, setAddonLedger] = useState<AddonLedgerItem[]>([]);

    const getOneTimePrice = (builder: BusinessBuilder) => {
        if (!builder.product) {
            return 0;
        }
        return GetTotalOneTimePrice(
            builder.product,
            builder.addons ?? [],
            builder.discount ?? 0,
            builder.productGroupId,
        );
    };

    const getAddonLedger = (builder: BusinessBuilder): AddonLedgerItem[] => {
        if (!builder.addons || !builder.productGroup || !builder.product) {
            return [];
        }

        return CreateAddonLedger(
            builder.addons,
            builder.productGroup,
            builder.product,
        );
    };

    useEffect(() => {
        setTotalOneTimePrice(getOneTimePrice(businessBuilder));
        setAddonLedger(getAddonLedger(businessBuilder));
    }, [businessBuilder]);

    useEffect(() => {
        let totalMonthly = 0;
        addonLedger.forEach((addon) => {
            if (!addon.startDate && !addon.contractLength) {
                totalMonthly += addon.recurringPrice;
            }
        });
        if (businessBuilder.product) {
            const discountedMonthly = GetDiscountPrice(
                businessBuilder.product.pricePerMonth,
                businessBuilder.discount ?? 0,
            );
            totalMonthly +=
                businessBuilder.product.contractLengthMonths > 1
                    ? discountedMonthly
                    : 0;
        }
        setTotalMonthlyPrice(totalMonthly);
    }, [addonLedger, businessBuilder.product, businessBuilder.discount]);

    return {
        productId: businessBuilder.productId ?? "",
        addons: businessBuilder.addons ?? [],
        totalOneTimePrice: totalOneTimePrice,
        totalMonthlyPrice: totalMonthlyPrice,
        discount: businessBuilder.discount,
        isOverrideSetupFee: businessBuilder.isOverrideSetupFee,
        overrideSetupFee: businessBuilder.overrideSetupFee,
        addonLedger: addonLedger,
        productDetails: businessBuilder.product,
        productGroup: businessBuilder.productGroup,
    };
};
